/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { createFilterOptions } from "@mui/material/useAutocomplete";
import {
  SmartAutoComplete,
  SmartDateTimePicker,
  SmartForm,
  SmartTextField,
  usePopup,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEventTypeApi, useMapAreaApi } from "~/api/GeneratedApi";
import LoadingBox from "~/components/LoadingBox";
import EventTypeCreationDialog from "~/pages/EventsPage/dialogs/EventTypeCreationDialog";
import { useAccess } from "~/utils/hooks/useAccess";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

export interface EventFormInputs {
  name: string;
  description: string;
  equipment: string;
  startTimestamp: Date;
  endTimestamp: Date;
  eventTypeId: number | null;
  mapAreaId: number | null;
}

interface Props {
  onSubmit: (data: EventFormInputs) => void;
  readOnly?: boolean;
  small?: boolean;
  defaultValues?: EventFormInputs;
}

const filter = createFilterOptions<number>();

const EventForm = ({
  onSubmit,
  readOnly,
  small,
  defaultValues,
}: Props): JSX.Element => {
  const memoedDefaultValues = React.useMemo(
    () =>
      defaultValues ?? {
        name: "",
        description: "",
        equipment: "",
        startTimestamp: new Date(),
        endTimestamp: new Date(),
        eventTypeId: null,
        mapAreaId: null,
      },
    [defaultValues],
  );
  const { t } = useTranslation("event-dialog");
  const { show } = usePopup();
  const { getEventTypes } = useEventTypeApi();
  const [typeValue, setTypeValue] = useState("");
  const formMethods = useSmartForm({ defaultValues: memoedDefaultValues });
  const { setValue } = formMethods;
  const { isGlobalAdmin } = useAccess();

  const {
    data: eventTypes,
    isLoading: typesLoading,
    refetch: fetchEventTypes,
  } = useCustomQuery("event-types", () => getEventTypes(), {
    onSuccess: (d) => {
      const type = d.results.find((et) => et.name === typeValue);
      if (type) {
        setValue("eventTypeId", type.id);
      }
    },
  });

  const { getMapAreas } = useMapAreaApi();

  const { isLoading: isLoadingAreas, data: mapAreas } = useCustomQuery(
    "map-areas",
    () => getMapAreas(),
  );

  if (isLoadingAreas) return <LoadingBox />;

  return (
    <SmartForm<EventFormInputs>
      onSubmit={onSubmit}
      gridProps={{ justifyContent: "center" }}
      methods={formMethods}
      formId="addEventForm"
    >
      <SmartTextField<EventFormInputs>
        name="name"
        label={t("event:name")}
        gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        required
        fullWidth
        autoComplete="off"
        InputProps={{ readOnly }}
        disabled={readOnly}
        autoFocus
      />
      <SmartTextField<EventFormInputs>
        name="description"
        label={t("event:description")}
        gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        fullWidth
        autoComplete="off"
        InputProps={{ readOnly }}
        disabled={readOnly}
      />
      <SmartTextField<EventFormInputs>
        name="equipment"
        label={t("event:equipment")}
        gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        fullWidth
        autoComplete="off"
        InputProps={{ readOnly }}
        disabled={readOnly}
      />
      <SmartDateTimePicker<EventFormInputs>
        name="startTimestamp"
        controllerProps={{
          rules: {
            required: { value: true, message: t("form:required") },
          },
        }}
        label={t("event:startTimestamp")}
        gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        textfieldProps={{ fullWidth: true }}
        InputProps={{ readOnly }}
        disabled={readOnly}
      />
      <SmartDateTimePicker<EventFormInputs>
        name="endTimestamp"
        controllerProps={{
          rules: {
            required: { value: true, message: t("form:required") },
          },
        }}
        label={t("event:endTimestamp")}
        gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
        textfieldProps={{ fullWidth: true }}
        InputProps={{ readOnly }}
        disabled={readOnly}
      />
      {!typesLoading && (
        <SmartAutoComplete<EventFormInputs, number, undefined, undefined, true>
          name="eventTypeId"
          controllerProps={{
            rules: {
              required: { value: true, message: t("form:required") },
            },
          }}
          label={t("event:event-type")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          options={eventTypes?.results.map((e) => e.id) ?? []}
          freeSolo
          getOptionLabel={(opt) => {
            if (opt >= 0) {
              return (
                eventTypes?.results.find((x) => x.id === opt)?.name ??
                t("general:unknown")
              );
            }
            if (opt === Number.NEGATIVE_INFINITY) {
              return !typeValue
                ? `(${t("general:add-new")})`
                : `(${t("general:add")} ${typeValue})`;
            }
            return "";
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            filtered.push(Number.NEGATIVE_INFINITY);

            return filtered;
          }}
          checkAdd={(val) => !eventTypes?.results.find((e) => e.id === val)}
          onAdd={() =>
            show(
              <EventTypeCreationDialog
                onSubmitted={(val) => {
                  setTypeValue(val);
                  fetchEventTypes();
                }}
                initialValue={typeValue}
              />,
              { noDialog: true },
            )
          }
          onInputChange={(_, v) => setTypeValue(v)}
          disabled={readOnly}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
        />
      )}
      <SmartAutoComplete<EventFormInputs, number>
        name="mapAreaId"
        label={t("map-area")}
        options={mapAreas?.results.map((x) => x.mapAreaId) ?? []}
        gridProps={{ xs: small ? 11 : 12 }}
        fullWidth
        disabled={isLoadingAreas || readOnly}
        controllerProps={{
          rules: {
            required: { value: !isGlobalAdmin, message: t("form:required") },
          },
        }}
        getOptionLabel={(opt) => {
          if (!(typeof opt === "number")) return "";
          return (
            mapAreas?.results.find((x) => x.mapAreaId === opt)?.name ??
            t("general:unknown")
          );
        }}
      />
    </SmartForm>
  );
};

export default EventForm;
